import { formatSalesOrderNumber } from '@zspace/format';
import { SalesOrderPermissions } from '@zspace/roles';
import { DeferredResponse, SalesOrderData } from '@zspace/types';
import { Suspense, useCallback } from 'react';
import { Columns, Section } from 'react-bulma-components';
import { FaDownload } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
} from 'react-router-dom';
import BackButton from '../../shared/back-button/back-button';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsZspaceInternalUser from '../../shared/hooks/is-zspace-internal-user';
import { useXLSXDownload } from '../../shared/hooks/xlsx-download';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SalesOrderDetailsLayout from '../sales-order-details-layout/sales-order-details-layout';
import {
  fetchSalesOrder,
  fetchSalesOrderDownloadableData,
} from '../sales-orders-service';
import styles from './sales-order-details-page.module.scss';

const EXPORT_SALES_ORDER_ERROR_MESSAGE =
  'The sales order could not be exported. Please try again';

export const loader: LoaderFunction = async ({ params }) => {
  const id = params.id;

  const response = fetchSalesOrder(id!);

  return defer({ response });
};

export function SalesOrderDetailsPageContent() {
  const salesOrder = useAsyncValue() as SalesOrderData;
  const isZspaceInternalUser = useIsZspaceInternalUser();
  const { executeHttpRequest, isLoading } = useHttpRequest();
  const { downloadXLSX } = useXLSXDownload();

  const handleExportSalesOrderClick = useCallback(
    async () =>
      executeHttpRequest({
        asyncFunction: async () => {
          const salesOrderDownloadableData =
            await fetchSalesOrderDownloadableData(salesOrder.id);
          downloadXLSX(salesOrderDownloadableData);
        },
        customErrorMessage: EXPORT_SALES_ORDER_ERROR_MESSAGE,
      }),
    [downloadXLSX, executeHttpRequest, salesOrder.id]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Section p={4}>
          <Columns display="flex" alignItems="center" paddingless>
            <Columns.Column
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <BackButton />
              <h1 className="is-size-3 has-text-weight-light">
                {formatSalesOrderNumber(salesOrder.number)} details
              </h1>
              <If condition={!!salesOrder.opportunity}>
                <h2 className="is-size-5 has-text-weight-light">
                  Opportunity - {salesOrder.opportunity?.name}
                </h2>
              </If>
            </Columns.Column>
            <Columns.Column display="flex" justifyContent="flex-end">
              <Button
                color="primary-dark"
                className={styles.exportButton}
                onClick={handleExportSalesOrderClick}
                isExecutingAction={isLoading}
              >
                <Button.LoadingIcon icon={FaDownload} />
                <span>Export</span>
              </Button>
            </Columns.Column>
          </Columns>
        </Section>
      }
    >
      <SalesOrderDetailsLayout
        salesOrder={salesOrder}
        adminViewMode={isZspaceInternalUser}
      />
    </BoxLayout>
  );
}

export function SalesOrderDetailsPage() {
  const { response } = useLoaderData() as DeferredResponse<SalesOrderData>;

  return (
    <ProtectedPage
      permissions={SalesOrderPermissions.SALES_ORDERS_SUMMARY_READ}
    >
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <SalesOrderDetailsPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default SalesOrderDetailsPage;
